import {scan} from 'react-scan';
import React, {StrictMode} from 'react';
import {observer} from 'mobx-react';
import {QueryClient, QueryClientProvider} from 'react-query';
import {ReactQueryDevtools} from 'react-query/devtools';
import {StyleProvider} from './ui/style/provider';
import {App} from './app';
import {installRoutes} from './routes/install-routes';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import {PostHogErrorBoundary, PostHogProvider} from 'posthog-js/react';
import {resolvePostHogSettings} from './configs/posthog';

const {projectToken, options} = resolvePostHogSettings(process.env);

if (projectToken) {
  try {
    posthog.init(projectToken, options);
  } catch (error) {
    Sentry.captureException(error);
  }
}

const AppErrorFallback = () => (
  <main role="alert" style={{padding: '32px', fontFamily: 'sans-serif'}}>
    <h1>Something went wrong</h1>
    <p>Please reload the page. If the problem continues, contact support.</p>
    <button onClick={() => window.location.reload()} type="button">
      Reload page
    </button>
  </main>
);

export function installClient() {
  const {Routes} = installRoutes();

  const AppView = observer(() => <App children={<Routes />} />);
  const queryClient = new QueryClient();

  scan({
    enabled: true,
    log: true,
  });

  const Page = (
    <StrictMode>
      <PostHogProvider client={posthog}>
        <Sentry.ErrorBoundary>
          <PostHogErrorBoundary fallback={<AppErrorFallback />}>
            <QueryClientProvider client={queryClient}>
              <StyleProvider>
                <AppView />
                <ReactQueryDevtools />
              </StyleProvider>
            </QueryClientProvider>
          </PostHogErrorBoundary>
        </Sentry.ErrorBoundary>
      </PostHogProvider>
    </StrictMode>
  );

  return {
    Page,
  };
}
